import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { useNavigate } from 'react-router-dom';
import WebScrollView from '../components/WebScrollView';
import { Calendar, ChevronRight, User, Clock, CheckCircle2, XCircle, AlertCircle } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { getAccessibleHomeIds } from '../lib/homeMembership';
import { shadows } from '../lib/styles';

interface Visit {
  id: string;
  scheduled_date: string | null;
  status: string;
  visit_type: string;
  home_id: string;
  assigned_to: string | null;
  home?: {
    address: string;
    city: string;
    state: string;
  };
  service_provider?: {
    company_name: string;
  };
  total_tasks: number;
  completed_tasks: number;
}

const Visits = () => {
  const navigate = useNavigate();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'scheduled' | 'completed'>('all');

  useEffect(() => {
    fetchVisits();
  }, []);

  const fetchVisits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setLoading(false);
        return;
      }

      // Get user's homes
      const homeIds = await getAccessibleHomeIds(user.id);

      if (homeIds.length === 0) {
        setLoading(false);
        return;
      }

      // Fetch all visits for user's homes
      const { data: visitsData, error: visitsError } = await supabase
        .from('visits')
        .select(`
          id,
          scheduled_date,
          status,
          visit_type,
          home_id,
          assigned_to,
          homes (
            address,
            city,
            state
          ),
          service_providers (
            company_name
          )
        `)
        .in('home_id', homeIds)
        .order('scheduled_date', { ascending: false, nullsFirst: false });

      if (visitsError) throw visitsError;

      // Get task counts for each visit
      const visitsWithTasks = await Promise.all(
        (visitsData || []).map(async (visit) => {
          const { data: tasks } = await supabase
            .from('visit_tasks')
            .select('id, status')
            .eq('visit_id', visit.id);

          const totalTasks = tasks?.length || 0;
          const completedTasks = tasks?.filter(t => t.status === 'completed').length || 0;

          return {
            ...visit,
            home: Array.isArray(visit.homes) ? visit.homes[0] : visit.homes,
            service_provider: Array.isArray(visit.service_providers) 
              ? visit.service_providers[0] 
              : visit.service_providers,
            total_tasks: totalTasks,
            completed_tasks: completedTasks,
          };
        })
      );

      setVisits(visitsWithTasks);
    } catch (error) {
      console.error('Error fetching visits:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatVisitDate = (dateString: string | null) => {
    if (!dateString) return 'Date TBD';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' };
      case 'scheduled':
        return { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' };
      case 'in_progress':
        return { bg: '#fff7ed', text: '#ea580c', border: '#fed7aa' };
      case 'cancelled':
      case 'no_show':
        return { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' };
      case 'requested':
        return { bg: '#faf5ff', text: '#8b5cf6', border: '#e9d5ff' };
      default:
        return { bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle2 size={16} color="#16a34a" />;
      case 'cancelled':
      case 'no_show':
        return <XCircle size={16} color="#dc2626" />;
      case 'requested':
        return <AlertCircle size={16} color="#8b5cf6" />;
      case 'proposed':
        return <AlertCircle size={16} color="#ea580c" />;
      default:
        return <Calendar size={16} color="#2563eb" />;
    }
  };

  const getStatusLabel = (status: string) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const filteredVisits = visits.filter(visit => {
    if (filter === 'all') return true;
    if (filter === 'scheduled') return visit.status === 'scheduled' || visit.status === 'requested' || visit.status === 'proposed';
    if (filter === 'completed') return visit.status === 'completed';
    return true;
  });

  if (loading) {
    return (
      <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        <View style={styles.header}>
          <Text style={styles.title}>All Visits</Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#9ca3af" />
        </View>
      </WebScrollView>
    );
  }

  return (
    <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>All Visits</Text>
          <Text style={styles.subtitle}>View and manage your HoneyDone visits</Text>
        </View>
      </View>

      {/* Filter Tabs */}
      <View style={styles.filterTabs}>
        <Pressable
          onPress={() => setFilter('all')}
          style={[styles.filterTab, filter === 'all' && styles.filterTabActive]}
        >
          <Text style={[styles.filterTabText, filter === 'all' && styles.filterTabTextActive]}>
            All ({visits.length})
          </Text>
        </Pressable>
        <Pressable
          onPress={() => setFilter('scheduled')}
          style={[styles.filterTab, filter === 'scheduled' && styles.filterTabActive]}
        >
          <Text style={[styles.filterTabText, filter === 'scheduled' && styles.filterTabTextActive]}>
            Upcoming ({visits.filter(v => v.status === 'scheduled' || v.status === 'proposed' || v.status === 'requested').length})
          </Text>
        </Pressable>
        <Pressable
          onPress={() => setFilter('completed')}
          style={[styles.filterTab, filter === 'completed' && styles.filterTabActive]}
        >
          <Text style={[styles.filterTabText, filter === 'completed' && styles.filterTabTextActive]}>
            Completed ({visits.filter(v => v.status === 'completed').length})
          </Text>
        </Pressable>
      </View>

      {/* Visits List */}
      {filteredVisits.length === 0 ? (
        <View style={styles.emptyState}>
          <Calendar size={48} color="#9ca3af" />
          <Text style={styles.emptyTitle}>No visits found</Text>
          <Text style={styles.emptyDescription}>
            {filter === 'all' 
              ? 'You don\'t have any visits yet. Start planning your first visit from the dashboard.'
              : `No ${filter} visits to display.`}
          </Text>
          {filter === 'all' && (
            <Pressable
              onPress={() => navigate('/visit-plan')}
              style={styles.planButton}
            >
              <Calendar size={16} color="#ffffff" />
              <Text style={styles.planButtonText}>Plan a Visit</Text>
            </Pressable>
          )}
        </View>
      ) : (
        <View style={styles.visitsList}>
          {filteredVisits.map((visit) => {
            const statusColors = getStatusColor(visit.status);
            
            return (
              <Pressable
                key={visit.id}
                onPress={() => navigate('/visit-plan', { state: { visitId: visit.id } })}
                style={({ pressed }) => [
                  styles.visitCard,
                  pressed && styles.visitCardPressed,
                ]}
              >
                <View style={styles.visitCardHeader}>
                  <View style={styles.visitCardHeaderLeft}>
                    <Calendar size={20} color="#2563eb" />
                    <View style={styles.visitCardHeaderText}>
                      <Text style={styles.visitDate}>{formatVisitDate(visit.scheduled_date)}</Text>
                      {visit.home && (
                        <Text style={styles.visitAddress}>
                          {visit.home.address}, {visit.home.city}
                        </Text>
                      )}
                    </View>
                  </View>
                  <View
                    style={[
                      styles.statusBadge,
                      { 
                        backgroundColor: statusColors.bg,
                        borderColor: statusColors.border,
                      },
                    ]}
                  >
                    {getStatusIcon(visit.status)}
                    <Text style={[styles.statusBadgeText, { color: statusColors.text }]}>
                      {getStatusLabel(visit.status)}
                    </Text>
                  </View>
                </View>

                <View style={styles.visitCardContent}>
                  {(visit.assigned_to || visit.service_provider) && (
                    <View style={styles.visitCardRow}>
                      <User size={16} color="#6b7280" />
                      <Text style={styles.visitCardLabel}>
                        {visit.service_provider?.company_name || visit.assigned_to}
                      </Text>
                    </View>
                  )}
                  
                  {visit.total_tasks > 0 && (
                    <View style={styles.visitCardRow}>
                      <CheckCircle2 size={16} color="#6b7280" />
                      <Text style={styles.visitCardLabel}>
                        {visit.completed_tasks} of {visit.total_tasks} tasks completed
                      </Text>
                    </View>
                  )}
                </View>

                <View style={styles.visitCardFooter}>
                  <Text style={styles.visitCardFooterText}>
                    {(visit.status === 'requested' || visit.status === 'proposed') ? 'Review and edit request' : 'View details'}
                  </Text>
                  <ChevronRight size={20} color="#9ca3af" />
                </View>
              </Pressable>
            );
          })}
        </View>
      )}
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  contentContainer: {
    padding: 16,
    gap: 24,
    ...(Platform.OS === 'web' && {
      paddingBottom: 32,
    }),
  },
  header: {
    gap: 4,
  },
  title: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#111827',
  },
  subtitle: {
    fontSize: 16,
    color: '#6b7280',
  },
  loadingContainer: {
    padding: 80,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterTabs: {
    flexDirection: 'row',
    gap: 8,
    backgroundColor: '#f9fafb',
    padding: 4,
    borderRadius: 8,
  },
  filterTab: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  filterTabActive: {
    backgroundColor: '#ffffff',
    ...shadows.sm,
  },
  filterTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
  },
  filterTabTextActive: {
    color: '#111827',
  },
  emptyState: {
    paddingVertical: 80,
    paddingHorizontal: 24,
    alignItems: 'center',
    gap: 16,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginTop: 8,
  },
  emptyDescription: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 20,
  },
  planButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  planButtonText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '600',
  },
  visitsList: {
    gap: 12,
  },
  visitCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    gap: 16,
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
      cursor: 'pointer',
      transition: 'all 0.2s',
    }),
  },
  visitCardPressed: {
    backgroundColor: '#f9fafb',
  },
  visitCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  visitCardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
  },
  visitCardHeaderText: {
    flex: 1,
    gap: 4,
  },
  visitDate: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  visitAddress: {
    fontSize: 14,
    color: '#6b7280',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  visitCardContent: {
    gap: 8,
  },
  visitCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  visitCardLabel: {
    fontSize: 14,
    color: '#6b7280',
  },
  visitCardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
  },
  visitCardFooterText: {
    fontSize: 13,
    color: '#9ca3af',
  },
});

export default Visits;

